import { Box, Avatar } from '@mui/material';
import {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import {
  Icon,
  Utils,
  Input,
  ReadFlow,
  ThemeContext,
  useSearchQuery,
  OfficeChatType,
} from '@gv/triage-components';

import { useAppSelector } from 'store';
import { ChatRequestType } from 'types';
import { useDefaultFolder } from 'hooks';
import * as Common from 'pages/office/inbox/styles';
import { getChatBubblePosition } from 'utils/storage';
import { useCreateMutation } from 'store/api/gv-chats';
import { useRealTimeChat } from 'context/real-time-chat';
import { useLazyDetailsQuery } from 'store/api/gv-chats';
import { useListQuery as useTeamListQuery } from 'store/api/users';
import { useReadUntilMessageMutation } from 'store/api/gv-messages';
import { selectAuthId, selectAuthForGVTalk } from 'store/slices/auth';
import { useDraggableChatBubble } from 'hooks/useDraggableChatBubble';
import { parseMessage } from 'pages/office/inbox/chat/chat-room/config';
import { useListQuery as useContactsListQuery } from 'store/api/gv-contacts';
import {
  useTyping,
  useMessages,
  useUnreadCount,
  useChatListEvents,
  useChatDetailsEvent,
} from 'hooks';

import * as Styles from './styles';

const { parseContact, getChatUserName } = Utils.Helpers;
const { formatDate } = Utils.Date;

interface RealTimeChatProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const RealTimeChat = ({ isOpen, onToggle }: RealTimeChatProps) => {
  const { isDarkTheme } = useContext(ThemeContext);
  const { setSelectedChat, selectedChat: contextSelectedChat } =
    useRealTimeChat();
  const isInitialMount = useRef(true);
  const prevIsOpen = useRef(isOpen);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  // Use the standard search query hook that's compatible with dark mode
  const { searchQuery, debounceQuery, setSearchQuery } = useSearchQuery();
  const searchTerm = debounceQuery;

  const gvTalkAuth = useAppSelector(selectAuthForGVTalk);
  const userId = useAppSelector(selectAuthId);

  // Get saved chat bubble position
  const savedPosition = useMemo(() => {
    if (!userId) {
      return undefined;
    }
    return getChatBubblePosition(userId);
  }, [userId]);

  // Use draggable hook
  const BUTTON_SIZE = 58; // Size of the floating button
  const { edge, position, hasMoved, isDragging, elementRef, handleMouseDown } =
    useDraggableChatBubble({
      buttonSize: BUTTON_SIZE,
      initialPosition: savedPosition,
      userId: userId ? String(userId) : undefined,
    });
  const chatId = contextSelectedChat?.chatId
    ? String(contextSelectedChat.chatId)
    : undefined;

  const { data: chatData } = useChatDetailsEvent({ id: chatId! }, !chatId);

  // Fetch chat list when no chat is selected
  // Note: We don't pass search to API to avoid server-side filtering issues
  // Instead, we filter client-side for more precise control
  const {
    data: chatListResponse,
    refetch: refetchChatList,
    isLoading: chatListLoading,
  } = useChatListEvents(
    {
      page: 1,
      limit: 50,
      type: ChatRequestType.All,
      // Don't pass search to API - we'll filter client-side for accuracy
    },
    !!chatId // Skip if a chat is selected
  );

  const { data: unreadCount = {}, refetch: refetchUnreadCount } =
    useUnreadCount();

  // Fetch contacts when searching (only if search term exists)
  const shouldSearchContacts = debounceQuery.trim().length > 0 && !chatId;
  const { data: contactsData, isLoading: contactsLoading } =
    useContactsListQuery(
      {
        page: 1,
        limit: 20,
        search: debounceQuery.trim() || undefined,
      },
      { skip: !shouldSearchContacts }
    );

  // Fetch team users to check online status when chat panel is open
  const shouldFetchTeamStatus = isOpen && !chatId;
  const { data: teamData, refetch: refetchTeamStatus } = useTeamListQuery(
    {
      page: 1,
      limit: 1000, // Get all users to check status
      activityStatus: ['active'],
    },
    { skip: !shouldFetchTeamStatus }
  );

  // Create a map of user IDs to online status
  // true = online, false = offline, undefined = not in team data
  const onlineStatusMap = useMemo(() => {
    if (!teamData?.users) {
      return new Map<number, boolean>();
    }
    const statusMap = new Map<number, boolean>();
    teamData.users.forEach((user: any) => {
      if (user.id) {
        // Map user status: true for online, false for offline
        statusMap.set(Number(user.id), user.status === 'online');
      }
    });
    return statusMap;
  }, [teamData]);

  // Refetch team status when navigating back from a chat
  useEffect(() => {
    if (isOpen && !chatId && shouldFetchTeamStatus) {
      refetchTeamStatus();
    }
  }, [isOpen, chatId, shouldFetchTeamStatus, refetchTeamStatus]);

  const { defaultFolderId } = useDefaultFolder();
  const [createChat] = useCreateMutation();

  const [readUntilMessage] = useReadUntilMessageMutation();

  // Refetch chat list when unread counts change to update individual chat unread badges
  useEffect(() => {
    if (!chatId && isOpen && Object.keys(unreadCount).length > 0) {
      // Refetch chat list when unread counts change to update badges in real-time
      const timer = setTimeout(() => {
        refetchChatList();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [unreadCount, chatId, isOpen, refetchChatList]);

  // Calculate total unread count across all chats
  const totalUnreadCount = useMemo(() => {
    if (!unreadCount || Object.keys(unreadCount).length === 0) {
      return 0;
    }
    return Object.values(unreadCount).reduce(
      (sum, count) => sum + (typeof count === 'number' ? count : 0),
      0
    );
  }, [unreadCount]);

  const [fetchChatDetails] = useLazyDetailsQuery();

  const currentChat = useMemo(
    () => (!chatId ? undefined : chatData),
    [chatData, chatId]
  );

  const {
    onReact,
    onRetry,
    onDeleteFile,
    onPinMessage,
    onTopReached,
    onMarkUnread,
    onEditMessage,
    onViewMessage,
    isNewerLoading,
    onBottomReached,
    onDeleteMessage,
    handleSendMessage,
    messages: messagesData,
    isLoading: messagesLoading,
  } = useMessages(currentChat, null, undefined);

  const { typers, onType } = useTyping(currentChat);

  // Refetch unread count when chat is opened/closed or when messages change
  useEffect(() => {
    if (chatId && messagesData?.length) {
      // When a chat is opened and messages are loaded:
      // 1. Refetch immediately to get current state
      // 2. Refetch again after readUntil completes (readUntil runs after 2 seconds)
      refetchUnreadCount();

      const timer = setTimeout(() => {
        refetchUnreadCount();
      }, 3000); // After readUntil completes (2 seconds) + buffer to ensure counts are updated
      return () => clearTimeout(timer);
    }
  }, [chatId, messagesData?.length, refetchUnreadCount]);

  // Refetch unread count when chat is closed or when going back to list
  // Use a longer delay to ensure readUntil has completed (readUntil runs after 2 seconds)
  useEffect(() => {
    if (!chatId && isOpen) {
      // When returning to chat list, refetch to get updated counts
      // Wait longer (3 seconds) to ensure readUntil has completed marking messages as read
      const timer = setTimeout(() => {
        refetchUnreadCount();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [chatId, isOpen, refetchUnreadCount]);

  const messages = useMemo(() => {
    if (!messagesData?.length) {
      return [];
    }
    return messagesData.map((msg) => parseMessage(msg));
  }, [messagesData]);

  // Setup mention users for @mentions functionality
  const mentionUsers = useMemo(() => {
    if (!currentChat?.members?.length) {
      return undefined;
    }

    const { authUserId, authClientId } = gvTalkAuth;

    const filtered = currentChat.members.filter((value) => {
      if (authUserId) {
        return authUserId !== value.userId;
      } else if (authClientId) {
        return authClientId !== value.clientId;
      }
      return true;
    });

    const items = filtered.map((member) => ({
      id: String(member.id),
      display: getChatUserName(parseContact(member) ?? {}),
    }));

    return items.length
      ? [{ display: `All`, id: 'mention-all-members' }, ...items]
      : [];
  }, [currentChat?.members, gvTalkAuth]);

  const onMentionClick = useCallback(() => {
    // Handle mention click if needed
  }, []);

  // Wrap handleSendMessage to ensure chatId is passed explicitly
  // Since we're not in a route with chatId, we need to pass it explicitly
  const wrappedHandleSendMessage = useCallback(
    async (values: any, resetForm?: () => void) => {
      if (!chatId) {
        return;
      }
      // Pass chatId explicitly as the 4th parameter (chatIdValue)
      await handleSendMessage(values, resetForm, undefined, chatId);
    },
    [handleSendMessage, chatId]
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      setShouldAnimate(false);
      prevIsOpen.current = isOpen;
      if (isOpen) {
        setHasBeenOpened(true);
      }
      return;
    }

    if (prevIsOpen.current !== isOpen) {
      if (isOpen) {
        setHasBeenOpened(true);
      }
      setShouldAnimate(true);
      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 500);
      prevIsOpen.current = isOpen;
      return () => clearTimeout(timer);
    }
    prevIsOpen.current = isOpen;
  }, [isOpen]);

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatLastMessageTime = (
    dateString: string | undefined
  ): string | null => {
    if (!dateString) {
      return null;
    }
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      // Show relative time for recent messages
      if (diffMins < 1) {
        return 'Just now';
      }
      if (diffMins < 60) {
        return `${diffMins}m ago`;
      }
      if (diffHours < 24) {
        return `${diffHours}h ago`;
      }
      if (diffDays < 7) {
        return `${diffDays}d ago`;
      }

      // For older messages, show date and time
      return formatDate(dateString, 'MMM DD, h:mm A');
    } catch {
      return formatDate(dateString, 'MMM DD, h:mm A');
    }
  };

  // Mark messages as read when exiting chat to ensure unread count updates
  const markMessagesAsRead = useCallback(async () => {
    if (!chatId || !messagesData?.length) {
      return;
    }

    try {
      // Find the last message ID
      const lastMessage = messagesData
        .slice()
        .reverse()
        .find((msg) => msg.id && !msg.isError && !msg.isSending);

      if (lastMessage?.id) {
        // Mark all messages up to the last one as read
        await readUntilMessage({
          chatId: chatId,
          flow: ReadFlow.Main,
          messageId: lastMessage.id,
        }).unwrap();
      }
    } catch (error) {
      // Silently handle errors - readUntil might have already been called
      console.error('Error marking messages as read:', error);
    }
  }, [chatId, messagesData, readUntilMessage]);

  const handleClose = async () => {
    // Mark messages as read before closing to ensure unread count updates
    await markMessagesAsRead();

    // Reset selected chat when closing
    setSelectedChat(null);

    // Refetch unread count after a delay to ensure it's updated
    setTimeout(() => {
      refetchUnreadCount();
    }, 1000);

    onToggle();
  };

  const handleBack = async () => {
    // Mark messages as read before going back to ensure unread count updates
    await markMessagesAsRead();

    // Reset selected chat when going back
    setSelectedChat(null);

    // Refetch unread count after a delay to ensure it's updated
    setTimeout(() => {
      refetchUnreadCount();
    }, 1000);
  };

  // Process chat list with unread counts and filter by search term
  const chatList = useMemo(() => {
    const rows = chatListResponse?.rows || [];
    if (!rows.length) {
      return [];
    }

    const processedChats = rows.map((chatItem: (typeof rows)[0]) => ({
      ...chatItem,
      unreadCount: unreadCount[chatItem.id] || 0,
    }));

    // If there's a search term, filter client-side strictly by what's actually displayed
    if (debounceQuery.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      return processedChats.filter((chatItem) => {
        const chatProps = Utils.GVTalk.getChatCardProps(chatItem, gvTalkAuth);
        // Use exactly what's displayed in the UI - chatTitle (same logic as rendering)
        const chatTitle = chatProps.chat.title || 'Chat';

        // Only match if the displayed title contains the search term
        // This ensures we only show chats where the visible name matches
        return chatTitle.toLowerCase().includes(searchLower);
      });
    }

    return processedChats;
  }, [chatListResponse?.rows, unreadCount, debounceQuery, gvTalkAuth]);

  // Get contact IDs that already have chats
  const existingChatContactIds = useMemo(() => {
    const contactIds = new Set<number>();
    chatList.forEach((chat) => {
      const chatProps = Utils.GVTalk.getChatCardProps(chat, gvTalkAuth);
      const firstMember = chatProps.chat.firstMember;
      if (firstMember) {
        const contactId =
          (firstMember as any).id || (firstMember as any).profileId;
        if (contactId) {
          contactIds.add(Number(contactId));
        }
      }
    });
    return contactIds;
  }, [chatList, gvTalkAuth]);

  // Filter contacts to only show those without existing chats and matching search
  const availableContacts = useMemo(() => {
    if (!contactsData?.rows) {
      return [];
    }
    let filtered = contactsData.rows.filter(
      (contact: { id: number }) => !existingChatContactIds.has(contact.id)
    );

    // If there's a search term, filter contacts by name only
    if (debounceQuery.trim()) {
      const searchLower = debounceQuery.toLowerCase().trim();
      filtered = filtered.filter((contact: any) => {
        const contactParsed = parseContact(contact);
        const contactName = getChatUserName(contactParsed ?? {});
        // Only match if the contact name contains the search term
        return contactName.toLowerCase().includes(searchLower);
      });
    }

    return filtered;
  }, [
    contactsData?.rows,
    existingChatContactIds,
    debounceQuery,
    parseContact,
    getChatUserName,
  ]);

  // Handle contact selection to create new chat
  const handleContactSelect = useCallback(
    async (contact: { id: number }) => {
      try {
        if (!defaultFolderId) {
          return;
        }

        // Create new chat with this contact
        const newChat = await createChat({
          type: OfficeChatType.Chat,
          folderId: defaultFolderId,
          members: [{ profileId: contact.id }],
        }).unwrap();

        if (newChat) {
          // Fetch chat details to get member information
          const result = await fetchChatDetails({ id: newChat.id });
          const chatDetails = result.data;

          if (!chatDetails) {
            return;
          }

          // Find the other member (the contact we just added)
          const { authUserId, authClientId } = gvTalkAuth;
          const otherMember = chatDetails.members?.find((member) => {
            if (authUserId) {
              return member.userId !== authUserId;
            } else if (authClientId) {
              return member.clientId !== authClientId;
            }
            return true;
          });

          if (otherMember) {
            const contactParsed = parseContact(otherMember);
            const memberPic =
              (otherMember as any).profile_pic ||
              (otherMember as any).avatar ||
              undefined;
            setSelectedChat({
              chatId: newChat.id,
              userId: otherMember.id,
              userName: getChatUserName(contactParsed ?? {}),
              userImageUrl: memberPic != null ? String(memberPic) : undefined,
              userRole:
                contactParsed?.role != null
                  ? String(contactParsed.role)
                  : undefined,
            });
          }

          // Refetch chat list to include the new chat
          refetchChatList();
        }
      } catch (error) {
        console.error('Error creating chat with contact:', error);
      }
    },
    [
      createChat,
      defaultFolderId,
      fetchChatDetails,
      gvTalkAuth,
      parseContact,
      getChatUserName,
      setSelectedChat,
      refetchChatList,
    ]
  );

  // Handle chat selection from list
  const handleChatSelect = useCallback(
    async (selectedChatItem: (typeof chatList)[0]) => {
      try {
        // Fetch chat details to get member information
        const result = await fetchChatDetails({ id: selectedChatItem.id });
        const chatDetails = result.data;

        if (!chatDetails) {
          return;
        }

        // Get chat props to determine if it's a group chat and get the title
        const chatProps = Utils.GVTalk.getChatCardProps(
          chatDetails,
          gvTalkAuth
        );
        const chatTitle = chatProps.chat.title || 'Chat';

        // Check if it's a group chat (more than 2 members)
        const members = chatDetails.members || [];
        const { authUserId, authClientId } = gvTalkAuth;

        // Count members excluding current user
        const otherMembers = members.filter((member) => {
          if (authUserId) {
            return member.userId !== authUserId;
          } else if (authClientId) {
            return member.clientId !== authClientId;
          }
          return true;
        });

        const isGroupChat = otherMembers.length > 1;

        if (isGroupChat) {
          // For group chats, use the chat title
          setSelectedChat({
            userId: 0,
            userName: chatTitle,
            userRole: undefined,
            userImageUrl: undefined,
            chatId: selectedChatItem.id,
          });
        } else if (otherMembers.length === 1) {
          // For one-on-one chats, use the other member's name
          const otherMember = otherMembers[0];
          const contact = parseContact(otherMember);
          const memberPic =
            (otherMember as any).profile_pic ||
            (otherMember as any).avatar ||
            undefined;
          setSelectedChat({
            userId: otherMember.id,
            chatId: selectedChatItem.id,
            userName: getChatUserName(contact ?? {}),
            userImageUrl: memberPic != null ? String(memberPic) : undefined,
            userRole: contact?.role != null ? String(contact.role) : undefined,
          });
        } else {
          // Fallback: use chat title
          setSelectedChat({
            userId: 0,
            userName: chatTitle,
            userRole: undefined,
            userImageUrl: undefined,
            chatId: selectedChatItem.id,
          });
        }
      } catch (error) {
        console.error('Error selecting chat:', error);
      }
    },
    [
      gvTalkAuth,
      setSelectedChat,
      fetchChatDetails,
      parseContact,
      getChatUserName,
    ]
  );

  const userName = contextSelectedChat?.userName || 'User';
  const userRole = contextSelectedChat?.userRole;
  const userImageUrl = contextSelectedChat?.userImageUrl;

  return (
    <>
      <Styles.FloatingButton
        edge={edge}
        isOpen={isOpen}
        ref={elementRef}
        isDragging={isDragging}
        isDarkTheme={isDarkTheme}
        onMouseDown={handleMouseDown}
        aria-label={isOpen ? 'Close Real Time Chat' : 'Open Real Time Chat'}
        style={{
          right: 'auto',
          bottom: 'auto',
          top: `${position.y}px`,
          left: `${position.x}px`,
        }}
        onClick={(e) => {
          // Only prevent click if we actually moved during drag
          // This allows normal clicks to work
          if (hasMoved) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (isOpen) {
            handleClose();
          } else {
            onToggle();
          }
        }}
      >
        <Icon.Chat />
        {!isOpen && totalUnreadCount > 0 && (
          <Styles.NotificationBadge isDarkTheme={isDarkTheme}>
            {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
          </Styles.NotificationBadge>
        )}
      </Styles.FloatingButton>

      {(hasBeenOpened || isOpen) && (
        <Styles.ChatPanel
          edge={edge}
          isOpen={isOpen}
          isDarkTheme={isDarkTheme}
          shouldAnimate={shouldAnimate}
        >
          <Styles.ChatHeader isDarkTheme={isDarkTheme}>
            <Box
              sx={{ flex: 1, gap: 1.5, display: 'flex', alignItems: 'center' }}
            >
              {contextSelectedChat && (
                <Styles.BackButton
                  aria-label="Back"
                  onClick={handleBack}
                  isDarkTheme={isDarkTheme}
                >
                  <Icon.ChevronLeft />
                </Styles.BackButton>
              )}
              {contextSelectedChat ? (
                <Box
                  sx={{
                    flex: 1,
                    gap: 1.5,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {userImageUrl ? (
                    <Avatar src={userImageUrl} sx={{ width: 32, height: 32 }} />
                  ) : (
                    <Styles.AvatarPlaceholder isDarkTheme={isDarkTheme}>
                      {getInitials(userName)}
                    </Styles.AvatarPlaceholder>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Styles.HeaderTitle isDarkTheme={isDarkTheme}>
                      {userName}
                    </Styles.HeaderTitle>
                    {userRole && (
                      <Styles.HeaderSubtitle isDarkTheme={isDarkTheme}>
                        {userRole}
                      </Styles.HeaderSubtitle>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    gap: 1,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Styles.HeaderTitle isDarkTheme={isDarkTheme}>
                    Team Communication
                  </Styles.HeaderTitle>
                  {totalUnreadCount > 0 && (
                    <Styles.UnreadCountBadge isDarkTheme={isDarkTheme}>
                      {totalUnreadCount > 99 ? '99+' : totalUnreadCount} unread
                    </Styles.UnreadCountBadge>
                  )}
                </Box>
              )}
            </Box>
            <Styles.CloseButton onClick={handleClose} isDarkTheme={isDarkTheme}>
              <Icon.Close />
            </Styles.CloseButton>
          </Styles.ChatHeader>

          {contextSelectedChat && chatId ? (
            <Styles.ChatContent
              onPaste={async (e) => {
                // Intercept paste events to handle image pasting
                const items = e.clipboardData?.items;
                if (!items) {
                  return;
                }

                // Check if any of the pasted items are images
                for (let i = 0; i < items.length; i++) {
                  const item = items[i];
                  if (item.type.indexOf('image') !== -1) {
                    // Get the image file
                    const file = item.getAsFile();
                    if (file) {
                      // Find the file input element in the ChatRoom component
                      // The ChatRoom component should have a file input for attachments
                      const fileInput = e.currentTarget.querySelector(
                        'input[type="file"]'
                      ) as HTMLInputElement;

                      if (fileInput) {
                        // Create a DataTransfer object to set files
                        const dataTransfer = new DataTransfer();
                        dataTransfer.items.add(file);

                        // Set the files on the input
                        fileInput.files = dataTransfer.files;

                        // Trigger change event to notify the ChatRoom component
                        const changeEvent = new Event('change', {
                          bubbles: true,
                          cancelable: true,
                        });
                        fileInput.dispatchEvent(changeEvent);

                        // Also trigger input event for better compatibility
                        const inputEvent = new Event('input', {
                          bubbles: true,
                          cancelable: true,
                        });
                        fileInput.dispatchEvent(inputEvent);
                      } else {
                        // If no file input found, allow default paste behavior
                        // The ChatRoom component might handle it internally
                        // but we log a warning for debugging
                        console.warn(
                          'File input not found in ChatRoom component'
                        );
                      }
                      // Don't prevent default - let ChatRoom handle it if it can
                      // Only prevent if we successfully handled it via file input
                      if (fileInput) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                      break;
                    }
                  }
                }
              }}
            >
              <Common.ChatRoom
                isGV
                task={{}}
                groupByDate
                typers={typers}
                onType={onType}
                isAdmin={false}
                onRetry={onRetry}
                onReact={onReact}
                messages={messages}
                hideForwardIcon={true}
                parseOptions={gvTalkAuth}
                isLoading={messagesLoading}
                onTopReached={onTopReached}
                onMarkUnread={onMarkUnread}
                onPinMessage={onPinMessage}
                mentionUsers={mentionUsers}
                onDeleteFile={onDeleteFile}
                defaultScrollToBottom={true}
                onViewMessage={onViewMessage}
                onEditMessage={onEditMessage}
                placeholder="Start type here"
                isNewerLoading={isNewerLoading}
                onMentionClick={onMentionClick}
                onBottomReached={onBottomReached}
                onDeleteMessage={onDeleteMessage}
                handleSendMessage={wrappedHandleSendMessage}
              />
            </Styles.ChatContent>
          ) : (
            <Styles.ChatContent>
              {chatListLoading ? (
                <Styles.EmptyState isDarkTheme={isDarkTheme}>
                  <Styles.EmptyStateText isDarkTheme={isDarkTheme}>
                    Loading chats...
                  </Styles.EmptyStateText>
                </Styles.EmptyState>
              ) : (
                <>
                  <Styles.SearchBarContainer isDarkTheme={isDarkTheme}>
                    <Input
                      search={false}
                      value={searchQuery}
                      leftIcon={<Icon.Search />}
                      placeholder="Search chats or contacts..."
                      onRightIconClick={() => setSearchQuery('')}
                      rightIcon={searchQuery ? <Icon.CloseCircle /> : undefined}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchQuery(e.target.value)
                      }
                    />
                  </Styles.SearchBarContainer>
                  {chatListLoading || contactsLoading ? (
                    <Styles.EmptyState isDarkTheme={isDarkTheme}>
                      <Styles.EmptyStateText isDarkTheme={isDarkTheme}>
                        Searching...
                      </Styles.EmptyStateText>
                    </Styles.EmptyState>
                  ) : chatList.length > 0 || availableContacts.length > 0 ? (
                    <Styles.ChatListContainer isDarkTheme={isDarkTheme}>
                      {/* Show existing chats */}
                      {chatList.map((chatItem: (typeof chatList)[0]) => {
                        const chatProps = Utils.GVTalk.getChatCardProps(
                          chatItem,
                          gvTalkAuth
                        );
                        const unread = chatItem.unreadCount || 0;
                        const chatTitle = chatProps.chat.title || 'Chat';
                        const firstMember = chatProps.chat.firstMember;
                        const avatarUrl =
                          firstMember &&
                          ((firstMember as any).profile_pic ||
                            (firstMember as any).avatar ||
                            (firstMember as any).photo);

                        const members = (chatItem as any).members || [];
                        const isOneToOneChat = members.length === 2;
                        let isOnline: boolean | null = null;

                        if (isOneToOneChat) {
                          const { authUserId, authClientId } = gvTalkAuth;

                          for (const member of members) {
                            const memberUserId =
                              member.user?.id || member.userId || member.id;
                            const memberClientId = member.clientId;

                            const isNotCurrentUser =
                              (authUserId && memberUserId !== authUserId) ||
                              (authClientId &&
                                memberClientId !== authClientId) ||
                              (!authUserId && !authClientId);

                            if (isNotCurrentUser && memberUserId) {
                              const userIdNum = Number(memberUserId);
                              if (onlineStatusMap.has(userIdNum)) {
                                isOnline =
                                  onlineStatusMap.get(userIdNum) === true;
                                break;
                              }
                            }
                          }
                        }

                        const lastMessage = (chatItem as any).lastMessage;
                        const lastMessageText = Utils.Helpers.safeRenderText(
                          lastMessage?.text || lastMessage?.msg_text
                        );
                        const lastMessageDate =
                          lastMessage?.createdAt || lastMessage?.created_at;
                        const formattedDateTime =
                          formatLastMessageTime(lastMessageDate);
                        return (
                          <Styles.ChatListItem
                            key={chatItem.id}
                            isDarkTheme={isDarkTheme}
                            onClick={() => handleChatSelect(chatItem)}
                          >
                            <Styles.ChatItemAvatar>
                              {avatarUrl ? (
                                <Avatar
                                  src={String(avatarUrl)}
                                  sx={{ width: 40, height: 40 }}
                                />
                              ) : (
                                <Styles.AvatarPlaceholder
                                  isDarkTheme={isDarkTheme}
                                >
                                  {getInitials(chatTitle)}
                                </Styles.AvatarPlaceholder>
                              )}
                              {isOneToOneChat && isOnline !== null && (
                                <Styles.OnlineIndicator isOnline={isOnline} />
                              )}
                            </Styles.ChatItemAvatar>
                            <Styles.ChatItemContent>
                              <Styles.ChatItemName isDarkTheme={isDarkTheme}>
                                {chatTitle}
                              </Styles.ChatItemName>
                              {lastMessageText ? (
                                <Styles.ChatItemSubtitleContainer>
                                  <Styles.ChatItemMessage
                                    isDarkTheme={isDarkTheme}
                                  >
                                    {lastMessageText}
                                  </Styles.ChatItemMessage>
                                  {formattedDateTime && (
                                    <Styles.ChatItemDateTime
                                      isDarkTheme={isDarkTheme}
                                    >
                                      {formattedDateTime}
                                    </Styles.ChatItemDateTime>
                                  )}
                                </Styles.ChatItemSubtitleContainer>
                              ) : chatProps.chat.firstMemberName ? (
                                <Styles.ChatItemSubtitle
                                  isDarkTheme={isDarkTheme}
                                >
                                  {chatProps.chat.firstMemberName}
                                </Styles.ChatItemSubtitle>
                              ) : null}
                            </Styles.ChatItemContent>
                            {unread > 0 && (
                              <Styles.ChatItemBadge isDarkTheme={isDarkTheme}>
                                {unread > 99 ? '99+' : unread}
                              </Styles.ChatItemBadge>
                            )}
                          </Styles.ChatListItem>
                        );
                      })}

                      {/* Show contacts without existing chats */}
                      {availableContacts.map((contact: { id: number }) => {
                        const contactParsed = parseContact(contact);
                        const contactName = getChatUserName(
                          contactParsed ?? {}
                        );
                        const contactPic =
                          (contact as any).profile_pic ||
                          (contact as any).avatar ||
                          (contact as any).photo ||
                          (contactParsed as any)?.profile_pic;
                        const contactUserId =
                          (contact as any).user?.id ||
                          (contact as any).userId ||
                          contact.id;
                        const contactIsOnline = contactUserId
                          ? onlineStatusMap.get(Number(contactUserId)) === true
                          : null;
                        const contactIsOffline = contactUserId
                          ? onlineStatusMap.has(Number(contactUserId)) &&
                            onlineStatusMap.get(Number(contactUserId)) !== true
                          : false;
                        const showContactStatus =
                          contactIsOnline === true || contactIsOffline === true;

                        return (
                          <Styles.ChatListItem
                            isDarkTheme={isDarkTheme}
                            key={`contact-${contact.id}`}
                            onClick={() => handleContactSelect(contact)}
                          >
                            <Styles.ChatItemAvatar>
                              {contactPic ? (
                                <Avatar
                                  src={String(contactPic)}
                                  sx={{ width: 40, height: 40 }}
                                />
                              ) : (
                                <Styles.AvatarPlaceholder
                                  isDarkTheme={isDarkTheme}
                                >
                                  {getInitials(contactName)}
                                </Styles.AvatarPlaceholder>
                              )}
                              {showContactStatus && (
                                <Styles.OnlineIndicator
                                  isOnline={contactIsOnline === true}
                                />
                              )}
                            </Styles.ChatItemAvatar>
                            <Styles.ChatItemContent>
                              <Styles.ChatItemName isDarkTheme={isDarkTheme}>
                                {contactName}
                              </Styles.ChatItemName>
                              {contactParsed?.role && (
                                <Styles.ChatItemSubtitle
                                  isDarkTheme={isDarkTheme}
                                >
                                  {contactParsed.role}
                                </Styles.ChatItemSubtitle>
                              )}
                            </Styles.ChatItemContent>
                            <Styles.NewChatBadge isDarkTheme={isDarkTheme}>
                              New
                            </Styles.NewChatBadge>
                          </Styles.ChatListItem>
                        );
                      })}
                    </Styles.ChatListContainer>
                  ) : (
                    <Styles.EmptyState isDarkTheme={isDarkTheme}>
                      <Icon.Chat style={{ fontSize: 48, opacity: 0.3 }} />
                      <Styles.EmptyStateText isDarkTheme={isDarkTheme}>
                        {debounceQuery.trim()
                          ? 'No chats found matching your search'
                          : 'No chats yet. Click on a message icon to start chatting'}
                      </Styles.EmptyStateText>
                    </Styles.EmptyState>
                  )}
                </>
              )}
            </Styles.ChatContent>
          )}
        </Styles.ChatPanel>
      )}
    </>
  );
};
